<template>
  <ul class="space-y-4">
    <li v-for="(entry, index) in displayedEntries" :key="`${entry.surface}-${index}`" class="flex gap-3">
      <span
        class="mt-0.5 inline-flex h-6 shrink-0 items-center rounded-theme-sm px-2 text-xs font-medium"
        :class="toneClass(entry.type)"
      >
        {{ t(`changelog.type.${entry.type}`) }}
      </span>
      <div class="min-w-0">
        <p class="text-sm font-medium leading-5 text-theme-text">
          {{ pickLocalized(entry.title_i18n, locale) }}
        </p>
        <p
          v-if="pickLocalized(entry.body_i18n, locale)"
          class="mt-1 text-sm leading-5 text-theme-text-secondary"
        >
          {{ pickLocalized(entry.body_i18n, locale) }}
        </p>
      </div>
    </li>
  </ul>
</template>

<script setup>
  /**
   * Renders a list of changelog entries. Shared by the dialog and the release notes
   * sidebar so the two cannot drift in how an entry reads.
   *
   * Entry text comes from the backend as `{ en, nl }` maps and is picked by the
   * active locale; the chrome around it (the type chip) is normal i18n.
   */
  import { pickLocalized } from '@/features/changelog/composables/useChangelog';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  const props = defineProps({
    entries: { type: Array, required: true },
  });

  const { t, locale: activeLocale } = useI18n();
  const locale = computed(() => activeLocale.value);

  const TYPE_ORDER = ['added', 'improved', 'fixed'];
  const displayedEntries = computed(() => {
    const seen = new Set();
    return [...props.entries]
      .sort((a, b) => TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type))
      .filter((entry) => {
        const key = JSON.stringify([entry.title_i18n, entry.body_i18n].map((values) =>
          pickLocalized(values, locale.value).trim().replace(/\s+/g, ' ').toLocaleLowerCase()
        ));
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  });

  const TONES = {
    added: 'bg-theme-palette-emerald/15 text-theme-palette-emerald',
    improved: 'bg-theme-palette-sky/15 text-theme-palette-sky',
    fixed: 'bg-theme-bg-subtle text-theme-text-secondary',
  };

  function toneClass(type) {
    return TONES[type] || TONES.fixed;
  }
</script>
